import { Injectable } from '@angular/core';

export type OnboardingStep =
    | 'verify-otp'
    | 'signup-details'
    | 'intro-survey'
    | 'pricing'
    | 'create-workspace'
    | 'create-project'
    | 'sdk-integration'
    | 'invitation'
    | 'completed';

type PendingStep = Exclude<OnboardingStep, 'completed'>;

const STORAGE_KEY = 'onboardingStep';

const STEP_ROUTES: Record<PendingStep, string> = {
    'verify-otp': '/account/verify-otp',
    'signup-details': '/account/signup-details',
    'intro-survey': '/get-started/intro-survey',
    'pricing': '/get-started/pricing',
    'create-workspace': '/get-started/create-workspace',
    'create-project': '/get-started/create-project',
    'sdk-integration': '/get-started/sdk-integration',
    'invitation': '/get-started/invitation',
};

const PRE_AUTH_ROUTES = new Set<string>([
    STEP_ROUTES['verify-otp'],
    STEP_ROUTES['signup-details'],
]);

/**
 * Single source of truth for "where is this browser in the onboarding wizard".
 * Backed by localStorage so it survives refreshes/redirects and is what
 * OnboardingGuard checks to stop users from jumping steps via URL edits.
 */
@Injectable({
    providedIn: 'root',
})
export class OnboardingStateService {
    setStep(step: PendingStep): void {
        localStorage.setItem(STORAGE_KEY, step);
    }

    markCompleted(): void {
        localStorage.setItem(STORAGE_KEY, 'completed');
    }

    getStep(): OnboardingStep | null {
        return (localStorage.getItem(STORAGE_KEY) as OnboardingStep) || null;
    }

    /**
     * The onboarding APIs key off `user_kuid`, historically read from `sessionStorage.userID`.
     * That value doesn't survive a closed tab, while `localStorage.currentUser` does (and is
     * what OnboardingGuard uses to resume the user on the right page) — so once signup-details
     * has run, fall back to `currentUser.kuid` instead of silently sending a null id.
     */
    getUserKuid(): string | null {
        const sessionId = sessionStorage.getItem('userID');
        if (sessionId) {
            return sessionId;
        }
        try {
            const currentUser = JSON.parse(localStorage.getItem('currentUser') || 'null');
            return currentUser?.kuid || null;
        } catch {
            return null;
        }
    }

    /**
     * Same durability problem as getUserKuid(): sessionStorage.workspaceToken is set at
     * create-workspace time but doesn't survive a closed tab. Read the plain string mirrored to
     * localStorage.workspaceToken instead of re-deriving it from localStorage.lastVisited_workspace
     * — that object's shape comes straight from the backend response and isn't a reliable place to
     * parse a field back out of.
     */
    getWorkspaceKuid(): string | null {
        return sessionStorage.getItem('workspaceToken') || localStorage.getItem('workspaceToken');
    }

    /** Display-only counterparts of getWorkspaceKuid() — same sessionStorage-first, localStorage-fallback pattern. */
    getWorkspaceTitle(): string | null {
        return sessionStorage.getItem('workspaceTitle') || localStorage.getItem('workspaceTitle');
    }

    getWorkspaceAvatarColor(): string | null {
        return (
            sessionStorage.getItem('workspaceAvatarColor') ||
            localStorage.getItem('workspaceAvatarColor')
        );
    }

    /**
     * Mirrors getWorkspaceKuid(): create-project's response is stashed in
     * sessionStorage.projectDetails, with a durable copy in localStorage.lastCreatedProject
     * (written alongside it) to survive a closed tab on the sdk-integration step.
     */
    getProjectDetails(): any | null {
        try {
            const sessionDetails = JSON.parse(sessionStorage.getItem('projectDetails') || 'null');
            if (sessionDetails) {
                return sessionDetails;
            }
            return JSON.parse(localStorage.getItem('lastCreatedProject') || 'null');
        } catch {
            return null;
        }
    }

    /**
     * Before signup-details succeeds there's no currentUser yet to fall back to, so the email
     * captured on the signup page is separately mirrored to localStorage.signupEmail — this
     * reads that durable copy if the sessionStorage one (per-tab) is gone.
     */
    getSignupEmail(): string | null {
        return sessionStorage.getItem('signupEmail') || localStorage.getItem('signupEmail');
    }

    getRouteForStep(step: OnboardingStep | null): string | null {
        if (!step || step === 'completed') {
            return null;
        }
        return STEP_ROUTES[step as PendingStep] || null;
    }

    isOnboardingRoutePath(path: string): boolean {
        return Object.prototype.hasOwnProperty.call(
            ROUTE_TO_STEP,
            path
        );
    }

    isPreAuthRoutePath(path: string): boolean {
        return PRE_AUTH_ROUTES.has(path);
    }

    /** Maps a backend `message.code` (returned by signin/signup/onboarding endpoints) to the step it implies. */
    stepFromMessageCode(code: string): OnboardingStep | null {
        switch (code) {
            case 'INCOMPLETE_ONBOARDING':
                return 'intro-survey';
            case 'NORMAL_USER_ONBOARDING':
            case 'REQUIRED_BILLING_INFORMATION':
                return 'pricing';
            case 'REQUIRED_WORKSPACE_CREATION':
                return 'create-workspace';
            case 'REQUIRED_PROJECT_CREATION':
                return 'create-project';
            case 'USER_INVITED_IN_WORKSPACE':
            case 'INVITED_USER_ONBOARDING':
                return 'invitation';
            case 'DASHBOARD':
                return 'completed';
            default:
                return null;
        }
    }
}

const ROUTE_TO_STEP: Record<string, PendingStep> = Object.entries(
    STEP_ROUTES
).reduce((acc, [step, route]) => {
    acc[route] = step as PendingStep;
    return acc;
}, {} as Record<string, PendingStep>);
